import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Film,
  Clock,
  Calendar,
  Globe,
  Search,
  Filter,
  Star,
  PlayCircle,
  ChevronDown,
  ChevronUp,
  Loader,
  Ticket,
  Tag,
  ArrowDownUp,
  CalendarDays,
  Info,
  X,
  Heart,
  Popcorn,
  Menu,
  Grid,
  List,
  Flame,
  TrendingUp,
} from "lucide-react";
import axios from "axios";

const MovieListingPage = () => {
  const [movies, setMovies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedGenre, setSelectedGenre] = useState("All");
  const [selectedLanguage, setSelectedLanguage] = useState("All");
  const [sortBy, setSortBy] = useState("releaseDate");
  const [sortOrder, setSortOrder] = useState("desc");
  const [expandedMovie, setExpandedMovie] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [animating, setAnimating] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [viewMode, setViewMode] = useState("grid"); // grid or list view
  const [activeTab, setActiveTab] = useState("nowShowing");

  const filtersRef = useRef(null);
  const navigate = useNavigate();

  // Fetch movie data
  useEffect(() => {
    const fetchMovies = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          "https://apigrand.dodunsoftsolutions.com/api/movie/all"
        );
        const data = response.data.data;
        console.log(data);

        const processedMovies = data.map((movie) => ({
          ...movie,
          rating: movie.rating || 4.0,
          genre: Array.isArray(movie.genre)
            ? movie.genre
            : movie.genre
              ? [movie.genre]
              : [],
        }));

        setMovies(processedMovies);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching movies:", err);
        setError("Failed to fetch movies. Please try again later.");
        setLoading(false);
      }
    };

    fetchMovies();

    // Load saved favorites
    const savedFavorites = localStorage.getItem("movieFavorites");
    if (savedFavorites) {
      setFavorites(JSON.parse(savedFavorites));
    }
  }, []);

  // Handle clicks outside filters panel
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        filtersRef.current &&
        !filtersRef.current.contains(event.target) &&
        !event.target.closest("[data-filter-toggle]")
      ) {
        setShowFilters(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [filtersRef]);

  // Get unique genres and languages for filters
  const allGenres = [...new Set(movies.flatMap((movie) => movie.genre))].sort();
  const allLanguages = [
    ...new Set(movies.map((movie) => movie.language)),
  ].sort();

  // Format date to readable string
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Format duration to hours and minutes
  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins > 0 ? `${mins}m` : ""}`;
  };

  // Toggle favorite status
  const toggleFavorite = (e, movieId) => {
    e.stopPropagation();

    setAnimating((prev) => [...prev, movieId]);
    setTimeout(() => {
      setAnimating((prev) => prev.filter((id) => id !== movieId));
    }, 700);

    const newFavorites = favorites.includes(movieId)
      ? favorites.filter((id) => id !== movieId)
      : [...favorites, movieId];

    setFavorites(newFavorites);
    localStorage.setItem("movieFavorites", JSON.stringify(newFavorites));
  };

  // Navigate to movie detail page
  const goToMovieDetail = (movieId) => {
    navigate(`/moviedetails/${movieId}`);
  };

  // Get filtered movies based on current filters
  const getFilteredMovies = () => {
    let filteredList = movies.filter((movie) => {
      const matchesStatus =
        activeTab === "nowShowing"
          ? movie.status === "nowShowing"
          : activeTab === "comingSoon"
            ? movie.status === "comingSoon"
            : activeTab === "ended"
              ? movie.status === "ended"
              : true;

      const matchesSearch = movie.movieName
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesGenre =
        selectedGenre === "All" || movie.genre.includes(selectedGenre);
      const matchesLanguage =
        selectedLanguage === "All" || movie.language === selectedLanguage;

      return matchesStatus && matchesSearch && matchesGenre && matchesLanguage;
    });

    // Optional: Additional filters
    if (activeTab === "trending") {
      filteredList = filteredList.filter((movie) => movie.rating >= 4.0);
    } else if (activeTab === "new") {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      filteredList = filteredList.filter(
        (movie) => new Date(movie.releaseDate) >= thirtyDaysAgo
      );
    } else if (activeTab === "favorites") {
      filteredList = filteredList.filter((movie) =>
        favorites.includes(movie._id)
      );
    }

    return filteredList.sort((a, b) => {
      if (sortBy === "releaseDate") {
        return sortOrder === "asc"
          ? new Date(a.releaseDate) - new Date(b.releaseDate)
          : new Date(b.releaseDate) - new Date(a.releaseDate);
      } else if (sortBy === "duration") {
        return sortOrder === "asc"
          ? a.duration - b.duration
          : b.duration - a.duration;
      } else if (sortBy === "rating") {
        return sortOrder === "asc" ? a.rating - b.rating : b.rating - a.rating;
      } else if (sortBy === "name") {
        return sortOrder === "asc"
          ? a.movieName.localeCompare(b.movieName)
          : b.movieName.localeCompare(a.movieName);
      }
      return 0;
    });
  };

  const filteredMovies = getFilteredMovies();

  // Toggle sort order
  const toggleSortOrder = () => {
    setSortOrder(sortOrder === "asc" ? "desc" : "asc");
  };

  // Toggle movie expand/collapse
  const toggleMovieExpand = (e, id) => {
    e.stopPropagation();
    if (expandedMovie === id) {
      setExpandedMovie(null);
    } else {
      setExpandedMovie(id);
    }
  };

  // Reset all filters
  const resetFilters = () => {
    setSearchTerm("");
    setSelectedGenre("All");
    setSelectedLanguage("All");
    setSortBy("releaseDate");
    setSortOrder("desc");
    setActiveTab("all");
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-b from-gray-50 to-white pt-24 pb-12">
        <div className="relative w-24 h-24 mb-6">
          <div className="absolute inset-0 bg-red-500 rounded-full opacity-10 animate-ping"></div>
          <div className="relative flex items-center justify-center w-24 h-24">
            <Loader className="w-12 h-12 text-red-600 animate-spin" />
          </div>
        </div>
        <p className="mt-4 text-xl font-medium text-gray-800">
          Loading Movies...
        </p>
        <p className="mt-2 text-gray-500">
          Preparing your cinematic experience
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-b from-gray-50 to-white pt-24 pb-12">
        <div className="p-8 max-w-md bg-white rounded-2xl shadow-xl border border-gray-200 text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-50 flex items-center justify-center">
            <X className="w-10 h-10 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Something Went Wrong
          </h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl shadow-md transition-all duration-300"
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-8">
      <div className="container mx-auto px-4">
        {/* Header with Glass Morphism */}
        <header className="mb-8 relative z-10">
          <div className="backdrop-blur-md bg-white/90 p-6 rounded-2xl shadow-lg border border-gray-200/50">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div className="mb-4 md:mb-0">
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2 flex items-center">
                  <div className="mr-3 p-2 bg-red-600 text-white rounded-lg">
                    <Film className="w-6 h-6" />
                  </div>
                  <span className="text-red-600 font-black">GRAND</span>
                  <span className="bg-clip-text text-transparent bg-gradient-to-r from-gray-800 to-gray-900">
                    CINEMAZ
                  </span>
                </h1>
                <p className="text-gray-600 text-lg flex items-center">
                  <Popcorn className="w-5 h-5 mr-2 text-amber-500" />
                  Premium Movie Experience
                </p>
              </div>

              <div className="flex flex-col md:items-end space-y-2">
                <div className="flex items-center gap-2">
                  <button
                    className={`p-2 rounded-lg ${viewMode === "grid"
                      ? "bg-gray-800 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    onClick={() => setViewMode("grid")}
                    title="Grid View"
                  >
                    <Grid size={18} />
                  </button>
                  <button
                    className={`p-2 rounded-lg ${viewMode === "list"
                      ? "bg-gray-800 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    onClick={() => setViewMode("list")}
                    title="List View"
                  >
                    <List size={18} />
                  </button>
                  <span className="ml-2 bg-red-50 px-3 py-1 rounded-full text-sm border border-red-100 text-red-800 font-medium">
                    {filteredMovies.length}{" "}
                    {filteredMovies.length === 1 ? "movie" : "movies"} available
                  </span>
                </div>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  data-filter-toggle="true"
                  className="flex items-center gap-2 text-sm bg-gradient-to-r from-amber-400 to-amber-500 hover:from-amber-500 hover:to-amber-600 text-white px-5 py-2.5 rounded-xl transition-all duration-300 shadow-md md:self-end"
                >
                  <Filter size={16} />
                  {showFilters ? "Hide Filters" : "Show Filters"}
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Category Tabs */}
        <div className="mb-6">
          <div className="flex overflow-x-auto hide-scrollbar space-x-2 py-2">
            <button
              onClick={() => setActiveTab("all")}
              className={`px-5 py-2.5 rounded-xl flex items-center whitespace-nowrap font-medium transition-all ${activeTab === "all"
                ? "bg-gray-800 text-white shadow-md"
                : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-200"
                }`}
            >
              <Menu className="mr-2" size={18} />
              All Movies
            </button>
            <button
              onClick={() => setActiveTab("nowShowing")}
              className={`px-5 py-2.5 rounded-xl flex items-center whitespace-nowrap font-medium transition-all ${activeTab === "trending"
                ? "bg-red-600 text-white shadow-md"
                : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-200"
                }`}
            >
              <TrendingUp className="mr-2" size={18} />
              Now Showing
            </button>
            <button
              onClick={() => setActiveTab("comingSoon")}
              className={`px-5 py-2.5 rounded-xl flex items-center whitespace-nowrap font-medium transition-all ${activeTab === "comingSoon"
                ? "bg-indigo-600 text-white shadow-md"
                : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-200"
                }`}
            >
              <Calendar className="mr-2" size={18} />
              Coming Soon
            </button>
            <button
              onClick={() => setActiveTab("favorites")}
              className={`px-5 py-2.5 rounded-xl flex items-center whitespace-nowrap font-medium transition-all ${activeTab === "favorites"
                ? "bg-amber-500 text-white shadow-md"
                : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-200"
                }`}
            >
              <Heart className="mr-2" size={18} />
              My Favorites ({favorites.length})
            </button>
          </div>
        </div>

        {/* Search Bar - Always Visible */}
        <div className="mb-6">
          <div className="relative">
            <Search
              className="absolute left-4 top-1/2 transform -translate-y-1/2 text-red-600"
              size={20}
            />
            <input
              type="text"
              placeholder="Search movies by title..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3.5 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 text-gray-700 placeholder-gray-400 shadow-md transition-all duration-300"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X size={18} />
              </button>
            )}
          </div>
        </div>

        {/* Filters Panel - Toggleable */}
        {showFilters && (
          <div
            ref={filtersRef}
            className="backdrop-blur-md bg-white/90 rounded-2xl p-6 mb-6 shadow-lg border border-gray-200/50 animate-fadeIn"
          >
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-medium text-gray-800 flex items-center">
                <Filter size={18} className="mr-2 text-red-600" /> Advanced
                Filters
              </h3>
              <button
                onClick={resetFilters}
                className="text-sm text-red-600 hover:text-red-700 flex items-center font-medium"
              >
                <ArrowDownUp size={14} className="mr-1" /> Reset All
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Genre
                </label>
                <div className="relative">
                  <select
                    value={selectedGenre}
                    onChange={(e) => setSelectedGenre(e.target.value)}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 text-gray-700 appearance-none shadow-sm"
                  >
                    <option value="All">All Genres</option>
                    {allGenres.map((genre) => (
                      <option key={genre} value={genre}>
                        {genre}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4">
                    <ChevronDown className="w-4 h-4 text-gray-500" />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Language
                </label>
                <div className="relative">
                  <select
                    value={selectedLanguage}
                    onChange={(e) => setSelectedLanguage(e.target.value)}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 text-gray-700 appearance-none shadow-sm"
                  >
                    <option value="All">All Languages</option>
                    {allLanguages.map((language) => (
                      <option key={language} value={language}>
                        {language}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4">
                    <ChevronDown className="w-4 h-4 text-gray-500" />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sort By
                </label>
                <div className="flex items-center gap-2">
                  <div className="relative flex-grow">
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 text-gray-700 appearance-none shadow-sm"
                    >
                      <option value="releaseDate">Release Date</option>
                      <option value="name">Name</option>
                      <option value="duration">Duration</option>
                      <option value="rating">Rating</option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4">
                      <ChevronDown className="w-4 h-4 text-gray-500" />
                    </div>
                  </div>

                  <button
                    onClick={toggleSortOrder}
                    className="p-3 bg-amber-500 text-white rounded-xl hover:bg-amber-600 transition-colors shadow-md"
                    aria-label={
                      sortOrder === "asc" ? "Sort descending" : "Sort ascending"
                    }
                  >
                    {sortOrder === "asc" ? (
                      <ChevronUp size={20} />
                    ) : (
                      <ChevronDown size={20} />
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Movies Display */}
        {filteredMovies.length === 0 ? (
          <div className="flex flex-col items-center justify-center bg-white rounded-2xl shadow-lg p-12 border border-gray-200 mt-8">
            <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center mb-4">
              <Film className="w-12 h-12 text-red-600" />
            </div>
            <h3 className="text-2xl font-medium text-gray-800 mb-3">
              No movies found
            </h3>
            <p className="text-gray-600 text-center max-w-md">
              {activeTab === "favorites"
                ? "You haven't added any favorites yet. Browse movies and click the heart icon to add them to your favorites."
                : "Try adjusting your search or filters to find what you're looking for"}
            </p>
            <button
              onClick={resetFilters}
              className="mt-6 px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-xl transition-all duration-300 font-medium shadow-md"
            >
              Reset All Filters
            </button>
          </div>
        ) : viewMode === "grid" ? (
          // Grid View
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredMovies.map((movie) => (
              <div
                key={movie._id}
                className="bg-white rounded-2xl shadow-lg overflow-hidden transform transition-all duration-300 hover:-translate-y-2 hover:shadow-xl border border-gray-200 group cursor-pointer"
                onClick={() => goToMovieDetail(movie._id)}
              >
                {/* Card Header with Gradient */}
                <div className="h-1.5 bg-gradient-to-r from-red-500 via-red-600 to-red-500"></div>

                {/* Movie Poster and Basic Info */}
                <div className="p-5">
                  <div className="flex">
                    <div className="mr-4 w-1/3">
                      <div className="aspect-[2/3] rounded-xl overflow-hidden shadow-md relative group">
                        <img
                          src={
                            movie.image
                              ? movie.image.startsWith("http")
                                ? movie.image
                                : `https://apigrand.dodunsoftsolutions.com${movie.image}`
                              : "/api/placeholder/180/270"
                          }
                          alt={movie.movieName}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                          onError={(e) => {
                            e.currentTarget.src = "/api/placeholder/180/270";
                          }}
                        />


                        {/* Badge for new releases */}
                        {new Date(movie.releaseDate) >=
                          new Date(Date.now() - 30 * 24 * 60 * 60 * 1000) && (
                            <div className="absolute top-2 left-0 bg-emerald-600 text-white text-xs font-bold py-1 px-2 rounded-r-lg shadow-md">
                              NEW
                            </div>
                          )}
                      </div>
                    </div>

                    <div className="w-2/3">
                      <div className="flex justify-between items-start">
                        <h2 className="text-xl font-bold text-gray-900 line-clamp-2 group-hover:text-red-600 transition-colors">
                          {movie.movieName}
                        </h2>
                        <span
                          className={`ml-2 px-2.5 py-1 text-xs font-bold rounded-full ${movie.certificate === "U"
                            ? "bg-green-100 text-green-800 border border-green-200"
                            : movie.certificate === "U/A"
                              ? "bg-blue-100 text-blue-800 border border-blue-200"
                              : movie.certificate === "PG-13"
                                ? "bg-yellow-100 text-yellow-800 border border-yellow-200"
                                : "bg-red-100 text-red-800 border border-red-200"
                            }`}
                        >
                          {movie.certificate}
                        </span>
                      </div>

                      <div className="flex items-center mt-2.5">
                        <div className="flex bg-gray-50 px-2 py-1 rounded-lg">
                          {[1].map((star) => (
                            <Star
                              key={star}
                              className={`w-4 h-4 ${star <= Math.round(movie.rating)
                                ? "text-amber-400 fill-amber-400"
                                : "text-gray-300"
                                }`}
                            />
                          ))}
                          <span className="ml-2 text-sm font-medium text-gray-700">
                            IMDb{" "}
                            {movie?.imdbRating
                              ? `${movie.imdbRating}/10`
                              : "--/10"}
                          </span>
                        </div>
                      </div>

                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {movie.genre.slice(0, 3).map((genre, idx) => (
                          <span
                            key={idx}
                            className="inline-block px-2 py-1 text-xs bg-red-50 text-red-800 rounded-full border border-red-100 font-medium"
                          >
                            {genre}
                          </span>
                        ))}
                        {movie.genre.length > 3 && (
                          <span className="inline-block px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded-full border border-gray-200">
                            +{movie.genre.length - 3}
                          </span>
                        )}
                      </div>

                      <div className="mt-3 text-sm text-gray-600 flex items-center">
                        <Clock className="w-4 h-4 mr-1.5 text-red-600" />
                        {formatDuration(movie.duration)}
                      </div>

                      <div className="mt-1.5 text-sm text-gray-600 flex items-center">
                        <Globe className="w-4 h-4 mr-1.5 text-red-600" />
                        {movie.language}
                      </div>

                      <div className="mt-1.5 text-sm text-gray-600 flex items-center">
                        <CalendarDays className="w-4 h-4 mr-1.5 text-red-600" />
                        {formatDate(movie.releaseDate)}
                      </div>
                    </div>
                  </div>

                  {/* View Details Button */}
                  <button
                    onClick={(e) => toggleMovieExpand(e, movie._id)}
                    className="mt-4 w-full py-2.5 px-4 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-medium rounded-xl flex items-center justify-center transition-all duration-300 shadow-md"
                  >
                    {expandedMovie === movie._id ? (
                      <>
                        Hide Details <ChevronUp className="ml-1.5" size={16} />
                      </>
                    ) : (
                      <>
                        View Details{" "}
                        <ChevronDown className="ml-1.5" size={16} />
                      </>
                    )}
                  </button>
                </div>

                {/* Expanded Details */}
                {expandedMovie === movie._id && (
                  <div className="px-5 pb-5 pt-1 border-t border-gray-200 animate-fadeIn">
                    <p className="mb-4 text-gray-700 text-sm leading-relaxed">
                      {movie.description ||
                        "No description available for this movie. Check back soon for updates!"}
                    </p>

                    <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 mb-4">
                      <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                        <Info size={16} className="mr-1.5 text-red-600" /> Movie
                        Details
                      </h4>
                      <ul className="space-y-2 text-sm">
                        <li className="flex items-start">
                          <span className="text-gray-600 w-24 flex-shrink-0 font-medium">
                            Director:
                          </span>
                          <span className="text-gray-900">
                            {movie.director || "Not available"}
                          </span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-gray-600 w-24 flex-shrink-0 font-medium">
                            Cast:
                          </span>
                          <span className="text-gray-900">
                            {movie.cast || "Not available"}
                          </span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-gray-600 w-24 flex-shrink-0 font-medium">
                            Format:
                          </span>
                          <span className="text-gray-900">
                            <span className="inline-block px-2 py-0.5 bg-gray-100 text-gray-800 rounded-md mr-1">
                              2D
                            </span>
                            <span className="inline-block px-2 py-0.5 bg-gray-100 text-gray-800 rounded-md mr-1">
                              3D
                            </span>
                            <span className="inline-block px-2 py-0.5 bg-gray-100 text-gray-800 rounded-md">
                              IMAX
                            </span>
                          </span>
                        </li>
                      </ul>
                    </div>

                    <div className="flex gap-3 mt-4">
                      <a
                        href={movie.trailerLink || "#"}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 py-2.5 px-4 bg-gray-800 hover:bg-gray-900 text-white rounded-xl flex items-center justify-center transition-all duration-300 shadow-md"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <PlayCircle className="mr-2" size={18} />
                        Watch Trailer
                      </a>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          // List View
          <div className="space-y-4">
            {filteredMovies.map((movie) => (
              <div
                key={movie._id}
                className="bg-white rounded-2xl shadow-lg overflow-hidden transform transition-all duration-300 hover:-translate-y-1 hover:shadow-xl border border-gray-200 group cursor-pointer"
                onClick={() => goToMovieDetail(movie._id)}
              >
                <div className="flex flex-col md:flex-row">
                  {/* Movie Poster */}
                  <div className="md:w-1/5 p-4">
                    <div className="aspect-[2/3] rounded-xl overflow-hidden shadow-md relative">
                      <img
                        src={
                          movie.image
                            ? movie.image.startsWith("http")
                              ? movie.image
                              : `https://apigrand.dodunsoftsolutions.com${movie.image}`
                            : "/api/placeholder/180/270"
                        }
                        alt={movie.movieName}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        onError={(e) => {
                          e.currentTarget.src = "/api/placeholder/180/270";
                        }}
                      />


                      {/* Badge for new releases */}
                      {new Date(movie.releaseDate) >=
                        new Date(Date.now() - 30 * 24 * 60 * 60 * 1000) && (
                          <div className="absolute top-2 left-0 bg-emerald-600 text-white text-xs font-bold py-1 px-2 rounded-r-lg shadow-md">
                            NEW
                          </div>
                        )}
                    </div>
                  </div>

                  {/* Movie Info */}
                  <div className="md:w-4/5 p-4 md:p-6 flex flex-col">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center">
                          <h2 className="text-2xl font-bold text-gray-900 group-hover:text-red-600 transition-colors mr-3">
                            {movie.movieName}
                          </h2>
                          <span
                            className={`px-2.5 py-1 text-xs font-bold rounded-full ${movie.certificate === "U"
                              ? "bg-green-100 text-green-800 border border-green-200"
                              : movie.certificate === "U/A"
                                ? "bg-blue-100 text-blue-800 border border-blue-200"
                                : movie.certificate === "PG-13"
                                  ? "bg-yellow-100 text-yellow-800 border border-yellow-200"
                                  : "bg-red-100 text-red-800 border border-red-200"
                              }`}
                          >
                            {movie.certificate}
                          </span>
                        </div>

                        <div className="flex items-center mt-2 mb-3">
                          <div className="flex bg-gray-50 px-2 py-1 rounded-lg">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star
                                key={star}
                                className={`w-4 h-4 ${star <= Math.round(movie.rating)
                                  ? "text-amber-400 fill-amber-400"
                                  : "text-gray-300"
                                  }`}
                              />
                            ))}
                            <span className="ml-2 text-sm font-medium text-gray-700">
                              {movie.rating}/5.0
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="hidden md:flex md:items-center md:space-x-2">
                        <button
                          onClick={(e) => toggleMovieExpand(e, movie._id)}
                          className="px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-medium rounded-xl flex items-center justify-center transition-all duration-300 shadow-md"
                        >
                          {expandedMovie === movie._id ? (
                            <>
                              Hide Details{" "}
                              <ChevronUp className="ml-1.5" size={16} />
                            </>
                          ) : (
                            <>
                              View Details{" "}
                              <ChevronDown className="ml-1.5" size={16} />
                            </>
                          )}
                        </button>
                        <button
                          className="px-4 py-2 bg-gradient-to-r from-amber-400 to-amber-500 hover:from-amber-500 hover:to-amber-600 text-white font-medium rounded-xl flex items-center justify-center transition-all duration-300 shadow-md"
                          onClick={(e) => {
                            e.stopPropagation();
                            // Add booking logic here
                          }}
                        >
                          <Ticket className="mr-1.5" size={18} />
                          Book Tickets
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-4 mt-2">
                      <div className="flex items-center text-sm text-gray-600">
                        <Clock className="w-4 h-4 mr-1.5 text-red-600 flex-shrink-0" />
                        {formatDuration(movie.duration)}
                      </div>

                      <div className="flex items-center text-sm text-gray-600">
                        <Globe className="w-4 h-4 mr-1.5 text-red-600 flex-shrink-0" />
                        {movie.language}
                      </div>

                      <div className="flex items-center text-sm text-gray-600">
                        <CalendarDays className="w-4 h-4 mr-1.5 text-red-600 flex-shrink-0" />
                        {formatDate(movie.releaseDate)}
                      </div>
                    </div>

                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {movie.genre.map((genre, idx) => (
                        <span
                          key={idx}
                          className="inline-block px-2 py-1 text-xs bg-red-50 text-red-800 rounded-full border border-red-100 font-medium"
                        >
                          {genre}
                        </span>
                      ))}
                    </div>

                    <p className="mt-3 text-gray-700 text-sm line-clamp-2">
                      {movie.description ||
                        "No description available for this movie. Check back soon for updates!"}
                    </p>

                    <div className="mt-4 md:hidden flex space-x-2">
                      <button
                        onClick={(e) => toggleMovieExpand(e, movie._id)}
                        className="flex-1 py-2 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-medium rounded-xl flex items-center justify-center transition-all duration-300 shadow-md"
                      >
                        {expandedMovie === movie._id ? (
                          <>
                            Hide Details{" "}
                            <ChevronUp className="ml-1.5" size={16} />
                          </>
                        ) : (
                          <>
                            View Details{" "}
                            <ChevronDown className="ml-1.5" size={16} />
                          </>
                        )}
                      </button>
                      <button
                        className="flex-1 py-2 bg-gradient-to-r from-amber-400 to-amber-500 hover:from-amber-500 hover:to-amber-600 text-white font-medium rounded-xl flex items-center justify-center transition-all duration-300 shadow-md"
                        onClick={(e) => {
                          e.stopPropagation();
                          // Add booking logic here
                        }}
                      >
                        <Ticket className="mr-1.5" size={18} />
                        Book
                      </button>
                    </div>
                  </div>
                </div>

                {/* Expanded Details */}
                {expandedMovie === movie._id && (
                  <div className="px-6 pb-6 pt-0 border-t border-gray-200 animate-fadeIn">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                      <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                        <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                          <Info size={16} className="mr-1.5 text-red-600" />{" "}
                          Movie Details
                        </h4>
                        <ul className="space-y-2 text-sm">
                          <li className="flex items-start">
                            <span className="text-gray-600 w-24 flex-shrink-0 font-medium">
                              Director:
                            </span>
                            <span className="text-gray-900">
                              {movie.director || "Not available"}
                            </span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-gray-600 w-24 flex-shrink-0 font-medium">
                              Cast:
                            </span>
                            <span className="text-gray-900">
                              {movie.cast || "Not available"}
                            </span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-gray-600 w-24 flex-shrink-0 font-medium">
                              Format:
                            </span>
                            <span className="text-gray-900">
                              <span className="inline-block px-2 py-0.5 bg-gray-100 text-gray-800 rounded-md mr-1">
                                2D
                              </span>
                              <span className="inline-block px-2 py-0.5 bg-gray-100 text-gray-800 rounded-md mr-1">
                                3D
                              </span>
                              <span className="inline-block px-2 py-0.5 bg-gray-100 text-gray-800 rounded-md">
                                IMAX
                              </span>
                            </span>
                          </li>
                        </ul>
                      </div>

                      <div>
                        <p className="mb-4 text-gray-700 text-sm leading-relaxed">
                          {movie.description ||
                            "No description available for this movie. Check back soon for updates!"}
                        </p>

                        <a
                          href={movie.trailerLink || "#"}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-full py-2.5 px-4 bg-gray-800 hover:bg-gray-900 text-white rounded-xl flex items-center justify-center transition-all duration-300 shadow-md"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <PlayCircle className="mr-2" size={18} />
                          Watch Trailer
                        </a>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Load More Button - if needed */}
        {filteredMovies.length > 9 && (
          <div className="text-center mt-10">
            <button className="px-8 py-3 bg-white hover:bg-gray-50 text-gray-800 font-medium rounded-xl transition-all duration-300 shadow-md border border-gray-300 inline-flex items-center">
              Load More Movies <ChevronDown className="ml-2" />
            </button>
          </div>
        )}
      </div>

      {/* Custom CSS for animations */}
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes heartbeat {
          0% {
            transform: scale(1);
          }
          15% {
            transform: scale(1.4);
          }
          30% {
            transform: scale(1);
          }
          45% {
            transform: scale(1.4);
          }
          60% {
            transform: scale(1);
          }
          100% {
            transform: scale(1);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out forwards;
        }

        .animate-heartbeat {
          animation: heartbeat 0.7s ease-in-out;
        }

        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
};

export default MovieListingPage;
